import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { PARTNERS_SITE_URL } from '../lib/links';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user, API_URL } = useAuth();
  const navigate = useNavigate();

  // Navigate once a session is restored
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
    }
  }, [user]);

  const [needsResume, setNeedsResume] = useState(false);
  const [sendingResume, setSendingResume] = useState(false);

  const resendApplicationLink = async () => {
    setSendingResume(true);
    try {
      await axios.post(`${API_URL}/api/auth/resume/resend`, { email });
      // Deliberately the same wording whether or not we found them, matching the endpoint.
      toast.success('If that application exists, the link is on its way.');
    } catch {
      toast.error('Could not send the link. Try again in a minute.');
    } finally {
      setSendingResume(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name}!`);
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      const detail = error.response?.data?.detail;

      // An applicant who never finished has no password, because the password is chosen at
      // the END of the application. They have not mistyped anything and "Forgot password"
      // cannot help them — it would reset a password that does not exist, and even with one
      // they would be sent straight back to the booking screen. The link is the only thing
      // that moves them, so offer to send it rather than telling them they got it wrong.
      if (detail?.reason === 'application_incomplete') {
        setNeedsResume(true);
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Invalid credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col justify-center px-6 py-12">
      <div className="w-full max-w-md mx-auto">
        <a href={PARTNERS_SITE_URL} className="flex w-fit items-center gap-2 text-[#64748B] hover:text-[#1A1A1A] mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Expedify Partners</span>
        </a>

        <a href={PARTNERS_SITE_URL} className="block w-fit mb-8">
          <img
            src="/light_logo.webp"
            alt="Expedify Logo"
            className="h-10 w-auto"
          />
        </a>

        <h1 className="text-3xl font-semibold text-[#1A1A1A] mb-2">
          Welcome back
        </h1>
        <p className="text-[#64748B] mb-8">
          Sign in to your partner account to continue.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-[#1A1A1A]">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1.5 h-12 rounded-lg border-[#E2E2DF] focus:ring-2 focus:ring-[#ef785b]/20 focus:border-[#ef785b]"
              required
              data-testid="login-email"
            />
          </div>

          {/* Shown only after the server has said this is an unfinished application, so it
              never appears speculatively and never hints at whether an address exists. */}
          {needsResume && (
            <div className="rounded-lg border border-[#ef785b]/30 bg-[#ef785b]/5 p-4">
              <p className="text-sm font-semibold text-[#1A1A1A]">
                Your application isn't finished
              </p>
              <p className="mt-1 text-sm leading-relaxed text-[#64748B]">
                That's where you choose a password, which is why signing in won't work yet.
                It takes about two minutes and you can stop and come back.
              </p>
              <button
                type="button"
                onClick={resendApplicationLink}
                disabled={sendingResume || !email}
                className="mt-3 h-10 rounded-lg bg-[#ef785b] px-4 text-sm font-semibold text-white
                           transition-colors hover:bg-[#e56847] disabled:opacity-60"
              >
                {sendingResume ? 'Sending…' : 'Email me the link to finish'}
              </button>
            </div>
          )}

          <div>
            <div className="flex items-baseline justify-between">
              <Label htmlFor="password" className="text-sm font-medium text-[#1A1A1A]">
                Password
              </Label>
              <Link
                to="/forgot-password"
                className="text-sm text-[#ef785b] font-medium hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative mt-1.5">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-12 rounded-lg border-[#E2E2DF] focus:ring-2 focus:ring-[#ef785b]/20 focus:border-[#ef785b] pr-10"
                required
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#1A1A1A]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#ef785b] hover:bg-[#D44017] text-white rounded-lg font-semibold btn-press"
            data-testid="login-submit"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <p className="mt-8 text-center text-sm text-[#64748B]">
          Run an agency and don&apos;t have an account yet?{' '}
          <Link to="/register" className="text-[#ef785b] font-semibold hover:underline" data-testid="register-link">
            Apply as a partner
          </Link>
        </p>

      </div>
    </div>
  );
};

export default LoginPage;
